import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';

import type { FileDto } from '@/api/api.dto';
import {
  API,
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
  putRequest,
} from '@/api/api.ts';
import { getServiceId } from '@/api/tokens.helper';
import { isNotFound } from '@/common/error';
import type { CreateEventPayload } from '@/components/CreateSheet/EditEventSheet/EditEventSheet';
import type {
  EventParticipant,
  EventResponse,
  EventsListResponse,
  GalleryImage,
  SubEventInput,
} from '@/store/events/events.dto.ts';
import { EVENTS } from '@/store/events/events.dto.ts';
import { hasAnyRole, OWNER_GROUP_ROLES } from '@/utils/roles';
import { runWithRetry } from '@/utils/runWithRetry';
import { toast } from '@/utils/toast';

import { useAuth } from '../auth/auth.store';
import type { TaskResponse } from '../tasks/tasks.dto';
import { useTasks } from '../tasks/tasks.store';

import { requestEventTagging } from './event-ai.api';
import type { ParticipantStatus } from './events.api';
import {
  cancelEvent,
  deleteEventAttachment,
  getEventByRoom,
  getEventDetail,
  getGuestSuggestions,
  getOutOfWaterEndForVessel,
  getOutOfWaterStatusForVessels,
  getPersonalEvents,
  getVesselGallery,
  renewEvent,
  setParticipantStatus,
  uploadCoverImage,
  uploadEventAttachments,
  uploadGalleryImage,
} from './events.api';

const updateEventParticipantsApi = async (id: string, participants: EventParticipant[]) => {
  await patchRequest(API.ACTIVITY, `/events/${id}/participants`, { participants });
};

export const deleteEventParticipantsApi = async (id: string, participantIds: string[]) => {
  await deleteRequest(API.ACTIVITY, `/events/${id}/participants`, { participants: participantIds });
};

type EventsState = {
  eventsList: EventsListResponse | null;
  currentEvent: EventResponse | null;
  eventsCount: number;
  isLoading: boolean;
  outOfWaterStatuses: Record<string, boolean>;
  outOfWaterEnds: Record<string, string>;
  fetchOutOfWaterStatuses: (ids: string[]) => Promise<void>;
  getAllEvents: () => Promise<void>;
  getEvents: (
    limit: number,
    page: number,
    append?: boolean,
    startsFrom?: string,
    endsTo?: string,
    tagId?: string,
    search?: string,
    type?: string,
  ) => Promise<EventResponse[] | undefined>;
  fetchEvents: (
    limit: number,
    page: number,
    startsFrom?: string,
    endsTo?: string,
    tagId?: string,
    search?: string,
    type?: string,
  ) => Promise<EventsListResponse>;
  clearEvents: () => void;
  /** `serviceId` is the space holding the event, when it is not the active vessel. */
  getEventById: (id: string, serviceId?: string) => Promise<void>;
  getEventByRoom: (tempChatId: string) => Promise<EventResponse>;
  /** Resolves to the created event, or `undefined` when the request failed. */
  createEvent: (body: CreateEventPayload) => Promise<EventResponse | undefined>;
  updateEvent: (body: EventResponse) => Promise<void>;
  updateEventParticipants: (id: string, participants: EventParticipant[]) => Promise<void>;
  removeEvent: (id: string) => Promise<void>;
  cancelEvent: (id: string, reason?: string) => Promise<void>;
  renewEvent: (id: string) => Promise<void>;
  getOutOfWaterStatusForVessels: (
    ids: string[],
    date: string,
  ) => Promise<{ [key: string]: boolean }>;
  deleteSubActivity: (id: string, parentEventId?: string) => Promise<void>;
  updateSubActivity: (
    id: string,
    data: SubEventInput,
    parentEvent: EventResponse | null,
  ) => Promise<void>;
  /**
   * Uploads an event's cover on its own, then puts the stored one on the event everywhere it shows.
   * Resolves to that stored cover, for a screen holding its own copy of the event.
   */
  uploadCoverImage: (id: string, file: File) => Promise<FileDto | null>;
  uploadEventAttachments: (id: string, file: File[]) => Promise<void>;
  deleteEventAttachment: (id: string, attachmentId: string, vesselId: string) => Promise<void>;
  setParticipantStatus: (
    id: string,
    status: ParticipantStatus,
    declineReason?: string,
  ) => Promise<void>;
  error: string | null;
  uploadGalleryImage: (id: string, file: File) => Promise<void>;
  getVesselGallery: () => Promise<GalleryImage[]>;
};

const refresh = (get: () => EventsState) => get().getAllEvents();

// How long the AI tagger usually takes to put its tags on a saved event.
const AI_TAGS_SETTLE_MS = 5000;

/**
 * Puts the event as the server has it now into the list (and the open event).
 * The calendar filters by tag on this list, so it must carry the event's tags.
 */
const putIntoList = (event: EventResponse) =>
  useEvents.setState((state) => ({
    eventsList: state.eventsList && {
      ...state.eventsList,
      items: state.eventsList.items.map((item) => (item.id === event.id ? event : item)),
    },
    currentEvent: state.currentEvent?.id === event.id ? event : state.currentEvent,
  }));

/**
 * The latest participant reload asked for each event. An RSVP PATCHes the event and its
 * sub-activities in parallel, so an earlier reload resolving last would bring back statuses from
 * before the later PATCHes.
 */
const participantReloads = new Map<string, number>();

const reloadIntoList = (id: string) =>
  getRequest<EventResponse>(API.ACTIVITY, `/events/${id}`)
    .then((res) => putIntoList(res.data))
    .catch((err) => console.error('Event reload error:', err));

/**
 * Fire-and-forget: the save has already succeeded, and the nightly job tags
 * anything this misses. Sub-activities are not tagged, only the event they belong to.
 * `reviewed`: the tags came through the create form's tag step, so they are the
 * user's and the AI only takes note — even of an event the user left with none.
 */
const queueEventTagging = (event: EventResponse, reviewed = false) => {
  const userId = useAuth.getState().session?.id;
  if (event.parent || !event.vessel_id || !userId) return;
  requestEventTagging(event.id, {
    vessel_id: event.vessel_id,
    user_id: userId,
    ...(reviewed ? { reviewed } : {}),
  })
    .then(() => {
      // The AI's tags land a few seconds after the save: pick them up, or the
      // calendar's tag filter misses the event until a page reload.
      if (!reviewed) setTimeout(() => void reloadIntoList(event.id), AI_TAGS_SETTLE_MS);
    })
    .catch((err) => {
      console.error('Event tagging request error:', err);
    });
};

const isUserNotOwner = () => {
  const session = useAuth.getState().session;
  return !hasAnyRole(session?.userRoles, OWNER_GROUP_ROLES);
};

export const useEvents = create<EventsState>((set, get) => ({
  eventsList: null,
  currentEvent: null,
  eventsCount: 0,
  isLoading: false,
  error: null,
  getAllEvents: async () => {
    set({ isLoading: true });
    let searchParams = `?limit=1000&page=1`;

    if (isUserNotOwner()) {
      searchParams += `&my_events=true`;
    }

    const personId = useAuth.getState().session?.id;
    // On the person's own space the vessel list already holds their personal events.
    const personal =
      personId && personId !== getServiceId()
        ? getPersonalEvents(personId).catch((err: unknown) => {
            console.error('Personal events request error:', err);
            return [];
          })
        : Promise.resolve([]);

    try {
      const [response, personalEvents] = await Promise.all([
        getRequest<EventsListResponse>(API.ACTIVITY, `/events${searchParams}`),
        personal,
      ]);

      set({
        eventsList: {
          items: [...response.data.items, ...personalEvents],
          count: response.data.count + personalEvents.length,
        },
        eventsCount: response.data.count + personalEvents.length,
        error: null,
      });
    } finally {
      set({ isLoading: false });
    }
  },

  getEvents: async (
    limit: number,
    page: number,
    append = false,
    startsFrom?: string,
    endsTo?: string,
    tagId?: string,
    search?: string,
    type?: string,
  ) => {
    let searchParams = `?limit=${limit}&page=${page}`;

    if (startsFrom) {
      searchParams += `&from=${encodeURIComponent(startsFrom)}`;
    }

    if (endsTo) {
      searchParams += `&to=${encodeURIComponent(endsTo)}`;
    }

    if (tagId) {
      searchParams += `&tags=${encodeURIComponent(tagId)}`;
    }

    if (search) {
      searchParams += `&search=${encodeURIComponent(search)}`;
    }

    if (type) {
      searchParams += `&type=${encodeURIComponent(type)}`;
    }

    if (isUserNotOwner()) {
      searchParams += `&my_events=true`;
    }

    const response = await getRequest<EventsListResponse>(API.ACTIVITY, `/events${searchParams}`);

    const items = response.data.items;

    set((state) => {
      if (append && state.eventsList) {
        return {
          eventsList: {
            items: [...state.eventsList.items, ...items],
            count: response.data.count,
          },
          eventsCount: response.data.count,
          error: null,
        };
      }

      return {
        eventsList: response.data,
        eventsCount: response.data.count,
        error: null,
      };
    });

    return items;
  },

  fetchEvents: async (
    limit: number,
    page: number,
    startsFrom?: string,
    endsTo?: string,
    tagId?: string,
    search?: string,
    type?: string,
  ) => {
    let searchParams = `?limit=${limit}&page=${page}`;

    if (startsFrom) {
      searchParams += `&from=${encodeURIComponent(startsFrom)}`;
    }

    if (endsTo) {
      searchParams += `&to=${encodeURIComponent(endsTo)}`;
    }

    if (tagId) {
      searchParams += `&tags=${encodeURIComponent(tagId)}`;
    }

    if (search) {
      searchParams += `&search=${encodeURIComponent(search)}`;
    }

    if (type) {
      searchParams += `&type=${encodeURIComponent(type)}`;
    }

    const response = await getRequest<EventsListResponse>(API.ACTIVITY, `/events${searchParams}`);
    return response.data;
  },

  clearEvents: () => {
    set({ eventsList: null, eventsCount: 0 });
  },

  getEventById: async (id: string, serviceId?: string) => {
    const response = await getRequest<EventResponse>(
      API.ACTIVITY,
      `/events/${id}`,
      false,
      undefined,
      serviceId,
    );

    set({
      currentEvent: response.data,
      error: null,
    });
  },

  getEventByRoom: async (tempChatId: string) => {
    const event = await getEventByRoom(tempChatId);
    set({ currentEvent: event, error: null });
    return event;
  },

  createEvent: async (body: CreateEventPayload) => {
    try {
      const { tasks, ...eventPayload } = body;
      const response = await postRequest<EventResponse, Omit<CreateEventPayload, 'tasks'>>(
        API.ACTIVITY,
        `/events`,
        eventPayload,
      );

      const eventId = response.data.id;
      const createdEvent = response.data;

      if (body.attachments?.length) {
        uploadEventAttachments(eventId, body.attachments).catch((err) => {
          console.error('Attachments upload error:', err);
        });
      }

      set((state) => {
        if (!state.eventsList) {
          return {
            eventsList: {
              items: [createdEvent],
              count: 1,
            },
            currentEvent: createdEvent,
            eventsCount: 1,
            error: null,
          };
        }

        const nextItems = [createdEvent, ...state.eventsList.items];

        return {
          eventsList: {
            ...state.eventsList,
            items: nextItems,
            count: state.eventsList.count + 1,
          },
          currentEvent: createdEvent,
          eventsCount: state.eventsCount + 1,
          error: null,
        };
      });

      // Awaited and reported: the event stays created either way, but its cover only shows once
      // it is really in, and a failed upload says so with Retry rather than vanishing.
      const cover = body.cover_image;
      if (cover) {
        await runWithRetry(() => get().uploadCoverImage(eventId, cover));
      }

      if (body.sub_events?.length) {
        const subEventResults = await Promise.all(
          body.sub_events.map((se) =>
            postRequest<EventResponse, object>(API.ACTIVITY, `/events`, {
              title: se.title,
              description: se.description,
              location: createdEvent.location,
              type: EVENTS.GUEST,
              time_slot: { type: 'day_based', start: se.time_slot.start, end: se.time_slot.end },
              parent_id: createdEvent.id,
              // A sub-event seats the crew on its own, so the exclusions repeat.
              excluded_participants: body.excluded_participants,
            }),
          ),
        ).catch((err) => {
          console.error('Sub-event creation error:', err);
          return [];
        });

        const createdSubEvents = subEventResults.map((r) => r.data);

        if (createdSubEvents.length) {
          set((state) => ({
            eventsList: state.eventsList
              ? {
                  ...state.eventsList,
                  items: [...createdSubEvents, ...state.eventsList.items],
                  count: state.eventsList.count + createdSubEvents.length,
                }
              : { items: createdSubEvents, count: createdSubEvents.length },
            eventsCount: state.eventsCount + createdSubEvents.length,
          }));
        }
      }

      // After the itinerary: the tagger reads it too. Tags chosen in the form are the user's.
      queueEventTagging(createdEvent, body.tag_ids !== undefined);

      if (tasks?.length) {
        await Promise.all(
          tasks.map((task) =>
            useTasks.getState().createTask({
              ...task,
              event_id: createdEvent.id,
            }),
          ),
        ).catch((err) => {
          toast.error(
            err instanceof Error
              ? err.message
              : 'Couldn’t set up every task. Please check the tasks.',
          );
        });
      }

      if (createdEvent.type === EVENTS.OUT_OF_WATER) {
        get().fetchOutOfWaterStatuses([createdEvent.vessel_id]);
      }

      // The event exists by now: a failed reload must not report it as not created.
      try {
        await refresh(get);
        await get().getEventById(createdEvent.id);
        // The freshly read event wins over the list reload, which may come back
        // without it or without its tags.
        const fresh = get().currentEvent;
        if (fresh?.id === createdEvent.id) putIntoList(fresh);
      } catch (err) {
        console.error('Reload after create error:', err);
      }
      return createdEvent;
    } catch (err) {
      console.error('Create event error:', err);
      set({ error: err as string });
      return undefined;
    }
  },

  updateEvent: async (body: EventResponse) => {
    const coverImage = (body as unknown as CreateEventPayload).cover_image;
    const existing =
      get().currentEvent?.id === body.id
        ? get().currentEvent
        : get().eventsList?.items.find((e) => e.id === body.id);

    const existingParticipants = existing?.participants ?? [];
    const newParticipants = body.participants ?? [];

    const toAdd = newParticipants.filter(
      (p) => !existingParticipants.some((ep) => ep.person_id === p.person_id),
    );
    const toRemove = existingParticipants
      .filter((ep) => !newParticipants.some((p) => p.person_id === ep.person_id))
      .map((ep) => ep.person_id);

    if (toRemove.length > 0) {
      await deleteEventParticipantsApi(body.id, toRemove);
    }
    if (toAdd.length > 0) {
      await updateEventParticipantsApi(body.id, toAdd);
    }

    const payloadWithTasks = body as unknown as CreateEventPayload;
    const { tasks, deleted_task_ids, ...putPayload } = payloadWithTasks;

    const response = await putRequest<EventResponse, any>(API.ACTIVITY, `/events`, putPayload);

    // New tasks (temp-/no id) are created; preloaded ones (real id) are updated so
    // edits made in the modal persist. Re-creating the preloaded ones would duplicate.
    if (tasks?.length) {
      await Promise.all(
        tasks.map((task) => {
          const withEvent = { ...task, event_id: response.data.id };
          const isNew = !task.id || task.id.startsWith('temp-');
          return isNew
            ? useTasks.getState().createTask(withEvent)
            : useTasks.getState().updateTask(withEvent as unknown as TaskResponse);
        }),
      ).catch((err) => {
        console.error('Task save error on updateEvent:', err);
      });
    }

    // Tasks the user removed from the section (loaded baseline minus current list)
    if (deleted_task_ids?.length) {
      await Promise.all(deleted_task_ids.map((id) => useTasks.getState().removeTask(id))).catch(
        (err) => {
          console.error('Task delete error on updateEvent:', err);
        },
      );
    }

    if (coverImage) {
      await uploadCoverImage(body.id, coverImage);
      const refreshed = await getRequest<EventResponse>(API.ACTIVITY, `/events/${body.id}`);
      response.data = refreshed.data;
    }

    const attachments = (body as unknown as CreateEventPayload).attachments;
    if (attachments?.length) {
      await uploadEventAttachments(body.id, attachments);
    }

    const deletedAttachmentIds = (body as unknown as CreateEventPayload).deleted_attachment_ids;
    if (deletedAttachmentIds?.length && existing?.vessel_id) {
      await Promise.all(
        deletedAttachmentIds.map((attachmentId) =>
          deleteEventAttachment(body.id, attachmentId, existing.vessel_id),
        ),
      );
    }

    if (attachments?.length || deletedAttachmentIds?.length) {
      const refreshed = await getRequest<EventResponse>(API.ACTIVITY, `/events/${body.id}`);
      response.data = refreshed.data;
    }

    const newSubEvents = (body as unknown as CreateEventPayload).sub_events ?? [];
    const oldSubActivities = existing?.sub_activities ?? [];
    const newIds = new Set(newSubEvents.filter((se) => se.id).map((se) => se.id!));
    const toDeleteIds = oldSubActivities.filter((sa) => !newIds.has(sa.id)).map((sa) => sa.id);
    const toUpdate = newSubEvents.filter((se) => {
      if (!se.id) return false;
      const old = oldSubActivities.find((sa) => sa.id === se.id);
      return (
        old &&
        (old.title !== se.title ||
          old.description !== se.description ||
          old.time_slot.start !== se.time_slot.start ||
          old.time_slot.end !== se.time_slot.end)
      );
    });
    const toCreate = newSubEvents.filter((se) => !se.id);

    if (toDeleteIds.length || toUpdate.length || toCreate.length) {
      await Promise.all([
        ...toDeleteIds.map((id) => deleteRequest<EventResponse>(API.ACTIVITY, `/events/${id}`)),
        ...toUpdate.map((se) =>
          putRequest(API.ACTIVITY, `/events`, {
            id: se.id,
            title: se.title,
            description: se.description,
            type: response.data.type,
            location: response.data.location,
            parent_id: response.data.id,
            time_slot: { type: 'day_based', start: se.time_slot.start, end: se.time_slot.end },
          }),
        ),
        ...toCreate.map((se) =>
          postRequest(API.ACTIVITY, `/events`, {
            title: se.title,
            description: se.description,
            location: response.data.location,
            type: response.data.type,
            time_slot: { type: 'day_based', start: se.time_slot.start, end: se.time_slot.end },
            parent_id: response.data.id,
          }),
        ),
      ]);
      const refreshed = await getRequest<EventResponse>(API.ACTIVITY, `/events/${body.id}`);
      response.data = refreshed.data;
    }

    // After the itinerary, which the tagger reads too. An edit of the tags themselves
    // is the person's call, and the AI leaves those events alone anyway.
    if (putPayload.tag_ids === undefined) {
      queueEventTagging(response.data);
    }

    set((state) => {
      const updatedEvent = response.data;

      let nextEventsList = state.eventsList;

      if (nextEventsList) {
        nextEventsList = {
          ...nextEventsList,
          items: nextEventsList.items.map((item) =>
            item.id === updatedEvent.id ? updatedEvent : item,
          ),
        };
      }

      const nextCurrentEvent =
        state.currentEvent && state.currentEvent.id === updatedEvent.id
          ? updatedEvent
          : state.currentEvent;

      return {
        eventsList: nextEventsList,
        currentEvent: nextCurrentEvent,
        eventsCount: state.eventsCount,
        error: null,
      };
    });

    await refresh(get);

    if (get().currentEvent?.id === body.id) {
      await get().getEventById(body.id);
    }

    if (
      (existing?.type === EVENTS.OUT_OF_WATER || response.data.type === EVENTS.OUT_OF_WATER) &&
      response.data.vessel_id
    ) {
      void get().fetchOutOfWaterStatuses([response.data.vessel_id]);
    }
  },

  updateEventParticipants: async (id: string, participants: EventParticipant[]) => {
    await updateEventParticipantsApi(id, participants);
    await refresh(get);
  },

  removeEvent: async (id: string) => {
    const removing =
      get().currentEvent?.id === id
        ? get().currentEvent
        : get().eventsList?.items.find((e) => e.id === id);

    await deleteRequest<EventResponse>(API.ACTIVITY, `/events/${id}`);
    await get().getAllEvents();
    set((state) => ({
      currentEvent: state.currentEvent?.id === id ? null : state.currentEvent,
    }));

    if (removing?.type === EVENTS.OUT_OF_WATER && removing.vessel_id) {
      void get().fetchOutOfWaterStatuses([removing.vessel_id]);
    }
  },
  cancelEvent: async (id: string, reason?: string) => {
    await cancelEvent(id, reason);
    await refresh(get);
    await get().getEventById(id);
  },

  renewEvent: async (id: string) => {
    await renewEvent(id);
    await refresh(get);
    await get().getEventById(id);
  },

  getOutOfWaterStatusForVessels: async (
    ids: string[],
    date: string,
  ): Promise<{ [key: string]: boolean }> => {
    return await getOutOfWaterStatusForVessels(ids, date);
  },

  outOfWaterStatuses: {},
  outOfWaterEnds: {},
  fetchOutOfWaterStatuses: async (ids: string[]) => {
    if (ids.length === 0) return;
    const now = new Date();
    const date = new Date(now.getTime() - now.getTimezoneOffset()).toISOString();
    const result = await getOutOfWaterStatusForVessels(ids, date).catch(() => ({}));
    set({ outOfWaterStatuses: result });

    const ashore = Object.entries(result as Record<string, boolean>)
      .filter(([, isAshore]) => isAshore)
      .map(([id]) => id);
    if (ashore.length === 0) {
      set({ outOfWaterEnds: {} });
      return;
    }

    const ends = await Promise.all(
      ashore.map((id) =>
        getOutOfWaterEndForVessel(id, date)
          .then((end) => [id, end] as const)
          .catch(() => [id, null] as const),
      ),
    );

    set({
      outOfWaterEnds: Object.fromEntries(ends.filter(([, end]) => end)) as Record<string, string>,
    });
  },

  deleteSubActivity: async (id: string, parentEventId?: string) => {
    await deleteRequest<EventResponse>(API.ACTIVITY, `/events/${id}`);

    const withoutSub = (event: EventResponse) => ({
      ...event,
      sub_activities: (event.sub_activities ?? []).filter((sa) => sa.id !== id),
    });

    set((state) => ({
      eventsList: state.eventsList
        ? {
            ...state.eventsList,
            items: state.eventsList.items.filter((event) => event.id !== id).map(withoutSub),
          }
        : state.eventsList,
      currentEvent: state.currentEvent ? withoutSub(state.currentEvent) : state.currentEvent,
    }));

    await refresh(get);

    if (parentEventId) {
      void get().getEventById(parentEventId);
    }
  },

  updateSubActivity: async (id: string, data: SubEventInput, parentEvent: EventResponse | null) => {
    await putRequest(API.ACTIVITY, `/events`, {
      id,
      title: data.title,
      description: data.description,
      type: parentEvent?.type ?? 'general',
      location: parentEvent?.location,
      parent_id: parentEvent?.id,
      time_slot: {
        type: 'day_based',
        start: data.time_slot.start,
        end: data.time_slot.end,
      },
    });

    const mergeEventTimeSlot = (
      existing: EventResponse['time_slot'],
    ): EventResponse['time_slot'] =>
      existing
        ? { ...existing, start: data.time_slot.start, end: data.time_slot.end }
        : { id: '', type: 'day_based', start: data.time_slot.start, end: data.time_slot.end };

    set((state) => {
      const nextEventsList = state.eventsList
        ? {
            ...state.eventsList,
            items: state.eventsList.items.map((event) => {
              if (event.id === id) {
                return {
                  ...event,
                  title: data.title,
                  description: data.description,
                  time_slot: mergeEventTimeSlot(event.time_slot),
                };
              }

              return {
                ...event,
                sub_activities: event.sub_activities.map((sa) =>
                  sa.id === id
                    ? {
                        ...sa,
                        title: data.title,
                        description: data.description,
                        time_slot: { start: data.time_slot.start, end: data.time_slot.end },
                      }
                    : sa,
                ),
              };
            }),
          }
        : state.eventsList;

      const nextCurrentEvent =
        state.currentEvent?.id === id
          ? {
              ...state.currentEvent,
              title: data.title,
              description: data.description,
              time_slot: mergeEventTimeSlot(state.currentEvent.time_slot),
            }
          : state.currentEvent
            ? {
                ...state.currentEvent,
                sub_activities: state.currentEvent.sub_activities.map((sa) =>
                  sa.id === id
                    ? {
                        ...sa,
                        title: data.title,
                        description: data.description,
                        time_slot: { start: data.time_slot.start, end: data.time_slot.end },
                      }
                    : sa,
                ),
              }
            : state.currentEvent;

      return {
        eventsList: nextEventsList,
        currentEvent: nextCurrentEvent,
      };
    });

    await refresh(get);

    if (parentEvent?.id) {
      void get().getEventById(parentEvent.id);
    }
  },

  uploadCoverImage: async (id: string, file: File) => {
    await uploadCoverImage(id, file);
    const { data } = await getRequest<EventResponse>(API.ACTIVITY, `/events/${id}`);
    set((state) => ({
      eventsList: state.eventsList && {
        ...state.eventsList,
        items: state.eventsList.items.map((e) => (e.id === id ? { ...e, image: data.image } : e)),
      },
      currentEvent:
        state.currentEvent?.id === id
          ? { ...state.currentEvent, image: data.image }
          : state.currentEvent,
    }));
    return data.image;
  },
  uploadEventAttachments: async (id: string, files: File[]) => {
    await uploadEventAttachments(id, files);
    await refresh(get);
  },
  deleteEventAttachment: async (id: string, attachmentId: string, vesselId: string) => {
    await deleteEventAttachment(id, attachmentId, vesselId);
    await refresh(get);
  },
  setParticipantStatus: async (id: string, status: ParticipantStatus, declineReason?: string) => {
    await setParticipantStatus(id, status, declineReason);

    const currentUserId = useAuth.getState().session?.id;
    if (!currentUserId) return;

    // The event the panel shows: `id` may be one of its sub-activities.
    const { currentEvent, eventsList } = get();
    const holder = [currentEvent, ...(eventsList?.items ?? [])].find(
      (e) => e && (e.id === id || e.sub_activities.some((sa) => sa.id === id)),
    );

    set((state) => {
      const updateParticipants = (participants: EventParticipant[]) =>
        participants.map((p) => (p.person_id === currentUserId ? { ...p, status } : p));

      const updateEvent = (event: EventResponse): EventResponse => {
        if (event.id === id)
          return { ...event, participants: updateParticipants(event.participants) };
        return {
          ...event,
          sub_activities: event.sub_activities.map((sa) =>
            sa.id === id ? { ...sa, participants: updateParticipants(sa.participants) } : sa,
          ),
        };
      };

      return {
        eventsList: state.eventsList
          ? { ...state.eventsList, items: state.eventsList.items.map(updateEvent) }
          : state.eventsList,
        currentEvent: state.currentEvent ? updateEvent(state.currentEvent) : state.currentEvent,
      };
    });

    // Not getEventById: for a sub-activity it would make that the open event, and the panel
    // would fall back to its stale copy of the parent.
    const reloadId = holder?.id ?? id;
    const seq = (participantReloads.get(reloadId) ?? 0) + 1;
    participantReloads.set(reloadId, seq);
    getRequest<EventResponse>(
      API.ACTIVITY,
      `/events/${reloadId}`,
      false,
      undefined,
      holder?.vessel_id,
    )
      .then(({ data }) => {
        if (participantReloads.get(reloadId) === seq) putIntoList(data);
      })
      .catch((err) => console.error('Event reload error:', err));
  },
  uploadGalleryImage: async (id: string, file: File) => {
    await uploadGalleryImage(id, file);
  },
  getVesselGallery: async () => {
    return await getVesselGallery();
  },
}));

/**
 * Guests of the vessel's past events — the "From recent events" list in the event
 * wizard. Fetched only while the wizard is open; a cold-start vessel gets [].
 */
export const useGuestSuggestionsQuery = (enabled: boolean) => {
  const vesselId = getServiceId();
  return useQuery({
    queryKey: ['guest-suggestions', vesselId],
    queryFn: () => getGuestSuggestions(),
    enabled: enabled && !!vesselId,
    staleTime: 5 * 60 * 1000,
  });
};

/** One event by id — for a record elsewhere that links to it, such as a berth's out-of-water period. */
export const useEventDetailQuery = (id?: string) =>
  useQuery({
    queryKey: ['event', id],
    queryFn: () => getEventDetail(id!),
    enabled: !!id,
    // A deleted event stays deleted: asking three more times only holds the caller on "Loading…".
    retry: (failureCount, error) => !isNotFound(error) && navigator.onLine && failureCount < 3,
  });
